import { markRaw } from 'vue'
import ReachoutModal from '../components/ReachoutModal.vue'
import EmailDetailDialog from '../drawer/cards/EmailDetailDialog.vue'
import EventDetailDialog from '../drawer/cards/EventDetailDialog.vue'
import SignalDrawerDialog from '../drawer/cards/SignalDrawerDialog.vue'
import GongCallDetailDialog from '../drawer/cards/GongCallDetailDialog.vue'
import CampaignMemberDetailDialog from '../drawer/cards/CampaignMemberDetailDialog.vue'
import StoryDetailDialog from '../drawer/cards/StoryDetailDialog.vue'
import EngagementDetailDialog from '../drawer/cards/EngagementDetailDialog.vue'
import CallLogDetailDialog from '../drawer/cards/CallLogDetailDialog.vue'
import PlayStatusChangeDialog from '../drawer/cards/PlayStatusChangeDialog.vue'
import SequenceStepDetailDialog from '../drawer/cards/SequenceStepDetailDialog.vue'
import SalesforceOppDialog from '../drawer/cards/SalesforceOppDialog.vue'
import SalesforceCustomObjectDialog from '../drawer/cards/SalesforceCustomObjectDialog.vue'

/**
 * Timeline item -> detail dialog, for every activity type the account activity
 * endpoint can emit.
 *
 * Shared so that any surface showing activity opens the SAME dialog for the
 * same item. The drawer's activity card and the Engaged page's activity pills
 * both route through here; duplicating the mapping is how two surfaces end up
 * disagreeing about what clicking a reply does.
 */

export type TimelineDialog = {
  title: string
  component: unknown
  componentProps: Record<string, unknown>
}

export type ResolveOptions = {
  accountId?: string
  onClose: () => void
  /** Called after a modal mutates the item, so the host can refetch. */
  onUpdated?: () => void
}

export function resolveTimelineDialog(it: any, options: ResolveOptions): TimelineDialog | null {
  const type = String(it?.type || '').trim()
  const accountId = String(options.accountId || '').trim()
  const onClose = options.onClose
  const onUpdated = options.onUpdated || onClose

  // ── Agent items (legacy modals — they carry editing/approval flows) ──
  if (type === 'reachout_email' || type === 'reachout_voicemail' || type === 'reachout_call' || type === 'reachout_linkedin_connect') {
    return { title: '', component: markRaw(ReachoutModal), componentProps: { reachout: it, onClose, onUpdated } }
  }
  if (type === 'story') {
    return { title: '', component: markRaw(StoryDetailDialog), componentProps: { story: it, onClose } }
  }

  // ── CRM emails (SF, HS, PD) ──
  if (type === 'salesforce_email' || type === 'salesforce_email_in' || type === 'salesforce_email_out') {
    return {
      title: '',
      component: markRaw(EmailDetailDialog),
      componentProps: { itemType: type, accountId, emailId: String(it?.data?.id || it?.id || '').trim(), initialData: it?.data || null, onClose },
    }
  }
  if (type === 'hubspot_email_in' || type === 'hubspot_email_out'
    || type === 'pipedrive_email_in' || type === 'pipedrive_email_out') {
    return {
      title: '',
      component: markRaw(EmailDetailDialog),
      componentProps: { itemType: type, initialData: it?.data || null, onClose },
    }
  }

  if (type === 'salesforce_event') {
    return {
      title: '',
      component: markRaw(EventDetailDialog),
      componentProps: { accountId, eventId: String(it?.data?.id || it?.id || '').trim(), initialEvent: it?.data || null, onClose },
    }
  }

  // ── Signals (generic + inbound Gmail replies) ──
  if (type === 'signal') {
    const signal = it?.data ? { ...(it.data || {}), id: it.id } : it
    return { title: '', component: markRaw(SignalDrawerDialog), componentProps: { signal, onClose } }
  }

  // ── Sequence step advancement (a play status change carrying step indices) ──
  if (type === 'sales_play_status_change' && it?.data?.event_type === 'sequence_step') {
    return {
      title: '',
      component: markRaw(SequenceStepDetailDialog),
      componentProps: { data: it?.data || {}, createdAt: it?.created_at, onClose },
    }
  }

  // ── Play-scoped lifecycle / membership transitions ──
  if (type === 'sales_play_status_change') {
    return {
      title: '',
      component: markRaw(PlayStatusChangeDialog),
      componentProps: { data: it?.data || {}, createdAt: it?.created_at, onClose },
    }
  }

  // ── Human-dialer calls ──
  if (type === 'call_log') {
    return {
      title: '',
      component: markRaw(CallLogDetailDialog),
      componentProps: { data: it?.data || {}, createdAt: it?.created_at, onClose },
    }
  }

  if (type === 'gong_call') {
    return {
      title: '',
      component: markRaw(GongCallDetailDialog),
      componentProps: { callId: String(it?.id || it?.data?.gong_call_id || '').trim(), onClose },
    }
  }

  if (type === 'salesforce_campaign_member') {
    return { title: '', component: markRaw(CampaignMemberDetailDialog), componentProps: { item: it, onClose } }
  }

  if (type === 'salesforce_custom_object') {
    const record = it?.data || {}
    const objectName = String(record?.object_name || '').trim()
    return {
      title: '',
      component: markRaw(SalesforceCustomObjectDialog),
      componentProps: { record, objectName, accountId, onClose },
    }
  }

  if (type === 'salesforce_opportunity') {
    return {
      title: '',
      component: markRaw(SalesforceOppDialog),
      componentProps: { record: it?.data || {}, accountId, onClose },
    }
  }

  // ── HubSpot engagements / Pipedrive activities (calls, meetings, tasks, notes) ──
  if (type === 'hubspot_call' || type === 'hubspot_meeting' || type === 'hubspot_task' || type === 'hubspot_note' || type === 'hubspot_engagement'
    || type === 'pipedrive_call' || type === 'pipedrive_meeting' || type === 'pipedrive_task' || type === 'pipedrive_activity' || type === 'pipedrive_email') {
    return {
      title: '',
      component: markRaw(EngagementDetailDialog),
      componentProps: { itemType: type, data: it?.data || {}, createdAt: it?.created_at, onClose },
    }
  }

  return null
}
